import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { schedulingRules } from '../schedulingRules'

export type SheetSnap = keyof typeof schedulingRules.mobileSheetSnaps

const SNAP_ORDER: SheetSnap[] = ['peek', 'half', 'full']
const SNAP_LABEL: Record<SheetSnap, string> = { peek: 'peek', half: 'half height', full: 'full height' }

/**
 * Mobile jobs panel as a bottom sheet over the map with three snap points. Reports how many
 * pixels it covers of the map element so the map can pad fitBounds by exactly that amount.
 */
export function MobileJobsSheet({
  snap,
  onSnapChange,
  mapElement,
  onMapOverlapChange,
  title,
  children,
}: {
  snap: SheetSnap
  onSnapChange: (snap: SheetSnap) => void
  mapElement: HTMLElement | null
  onMapOverlapChange: (px: number) => void
  title: ReactNode
  children: ReactNode
}) {
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ startY: number; startHeight: number; pointerId: number } | null>(null)
  const [dragHeight, setDragHeight] = useState<number | null>(null)
  const suppressClickRef = useRef(false)
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === 'undefined' ? 800 : window.innerHeight))

  useEffect(() => {
    const update = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const snapHeight = Math.round(viewportHeight * schedulingRules.mobileSheetSnaps[snap])
  const height = dragHeight ?? snapHeight

  // Overlap = how much of the map's bottom edge the sheet hides.
  const reportOverlap = useCallback(() => {
    if (!mapElement) return
    const mapRect = mapElement.getBoundingClientRect()
    const sheetTop = window.innerHeight - snapHeight
    onMapOverlapChange(Math.max(0, Math.min(mapRect.bottom, window.innerHeight) - Math.max(sheetTop, mapRect.top)))
  }, [mapElement, onMapOverlapChange, snapHeight])

  useEffect(() => {
    reportOverlap()
    window.addEventListener('scroll', reportOverlap, { passive: true })
    return () => window.removeEventListener('scroll', reportOverlap)
  }, [reportOverlap])

  const nearestSnap = (px: number): SheetSnap => SNAP_ORDER.reduce((best, key) => (
    Math.abs(viewportHeight * schedulingRules.mobileSheetSnaps[key] - px) < Math.abs(viewportHeight * schedulingRules.mobileSheetSnaps[best] - px) ? key : best
  ), 'peek' as SheetSnap)

  const step = (direction: 1 | -1) => {
    const index = SNAP_ORDER.indexOf(snap)
    onSnapChange(SNAP_ORDER[Math.min(Math.max(index + direction, 0), SNAP_ORDER.length - 1)])
  }

  return (
    <div
      ref={sheetRef}
      role="region"
      aria-label="Jobs"
      className={cn('fixed inset-x-0 bottom-0 z-40 flex flex-col rounded-t-2xl border-t border-[#dfeae3] bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.12)]', dragHeight == null && 'transition-[height] duration-200 motion-reduce:transition-none')}
      style={{ height }}
    >
      <button
        type="button"
        aria-label={`Resize jobs sheet, currently ${SNAP_LABEL[snap]}. Use arrow keys or tap to change`}
        className="flex w-full touch-none flex-col items-center gap-1 pb-1 pt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#169b62]"
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false
            return
          }
          onSnapChange(snap === 'full' ? 'peek' : SNAP_ORDER[SNAP_ORDER.indexOf(snap) + 1])
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp') { event.preventDefault(); step(1) }
          if (event.key === 'ArrowDown') { event.preventDefault(); step(-1) }
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId)
          dragRef.current = { startY: event.clientY, startHeight: height, pointerId: event.pointerId }
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current
          if (!drag || drag.pointerId !== event.pointerId) return
          const next = drag.startHeight + (drag.startY - event.clientY)
          if (Math.abs(drag.startY - event.clientY) > 4) setDragHeight(Math.min(Math.max(next, viewportHeight * 0.2), viewportHeight * 0.95))
        }}
        onPointerUp={() => {
          const drag = dragRef.current
          dragRef.current = null
          if (!drag || dragHeight == null) return
          suppressClickRef.current = true
          onSnapChange(nearestSnap(dragHeight))
          setDragHeight(null)
        }}
      >
        <span className="h-1.5 w-10 rounded-full bg-[#cfd8d2]" aria-hidden="true" />
        <span className="text-[12px] font-semibold text-[#20352b]">{title}</span>
      </button>
      <div className="min-h-0 flex-1 overflow-hidden px-2 pb-2">{children}</div>
    </div>
  )
}
